import React, { useState, useEffect } from 'react';
import ReactECharts from 'echarts-for-react';
import { Text, Flex } from '@chakra-ui/react';
import ChartCard from './ChartCard';
import { analyticsService, SentimentTrendResponse } from '../../services/analyticsService';

interface SentimentTrendChartProps {
  businessIdentifier?: string;
  timeRange?: string;
}

const FONT_FAMILY = 'TT Interphases Pro, Inter, sans-serif';

const formatPeriod = (period: string): string => {
  try {
    const date = new Date(period);
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = String(date.getFullYear()).slice(2);
    return `${month} ${year}`;
  } catch {
    return period;
  }
};

const SentimentTrendChart: React.FC<SentimentTrendChartProps> = ({
  businessIdentifier,
  timeRange,
}) => {
  const [data, setData] = useState<SentimentTrendResponse | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        // Smart group_by: 7d→daily view, 30d→weekly, 90d→monthly
        const groupBy = timeRange === '7d' ? 'week' : timeRange === '30d' ? 'week' : 'month';
        const result = await analyticsService.getBusinessSentimentTrend(
          businessIdentifier,
          timeRange || '7d',
          groupBy
        );
        setData(result);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to fetch sentiment trend data';
        setError(message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [businessIdentifier, timeRange]);

  const getChartOption = () => {
    if (!data || !data.data || data.data.length === 0) return {};

    const periods = data.data.map((d) => formatPeriod(d.period));
    const scores = data.data.map((d) => d.avg_score);

    return {
      tooltip: {
        trigger: 'axis',
        textStyle: { fontFamily: FONT_FAMILY },
        formatter: (params: any) => {
          const idx = params[0]?.dataIndex;
          const item = data.data[idx];
          if (!item) return '';
          return `<b>${formatPeriod(item.period)}</b><br/>Avg Score: ${item.avg_score.toFixed(1)}<br/>Conversations: ${item.conversation_count}`;
        },
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '10%',
        top: '10%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: periods,
        axisLabel: {
          fontFamily: FONT_FAMILY,
          fontSize: 11,
          color: '#666',
        },
        axisLine: { lineStyle: { color: '#E2E8F0' } },
        boundaryGap: false,
      },
      yAxis: {
        type: 'value',
        min: 0,
        max: 100,
        name: 'Avg Score',
        nameTextStyle: { fontFamily: FONT_FAMILY, fontSize: 11, color: '#999' },
        axisLabel: {
          fontFamily: FONT_FAMILY,
          fontSize: 11,
          color: '#999',
        },
        splitLine: { lineStyle: { color: '#EDF2F7' } },
      },
      series: [
        {
          type: 'line',
          data: scores,
          smooth: true,
          symbol: 'circle',
          symbolSize: 6,
          lineStyle: {
            color: '#4A90D9',
            width: 2,
          },
          itemStyle: {
            color: '#4A90D9',
          },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: 'rgba(74, 144, 217, 0.3)' },
                { offset: 1, color: 'rgba(74, 144, 217, 0.02)' },
              ],
            },
          },
        },
      ],
    };
  };

  const hasData = data && data.data && data.data.length > 0;

  return (
    <ChartCard title="Sentiment Trend Over Time" isLoading={isLoading} error={error}>
      {hasData ? (
        <ReactECharts
          option={getChartOption()}
          style={{ height: '350px', width: '100%' }}
          opts={{ renderer: 'svg' }}
        />
      ) : (
        <Flex align="center" justify="center" minH="200px">
          <Text color="gray.400" fontSize="sm" fontFamily={FONT_FAMILY}>
            No data available
          </Text>
        </Flex>
      )}
    </ChartCard>
  );
};

export default SentimentTrendChart;
